import "src/api/register-fetch";
import * as Sentry from "@sentry/tanstackstart-react";
import {
  type AnyRoute,
  type AnyRouter,
  createRouter,
  type ParsedLocation,
} from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { type PropsWithChildren } from "react";
import { I18nextProvider } from "react-i18next";

import { DefaultCatchBoundary } from "src/components/DefaultCatchBoundary";
import { NotFound } from "src/components/NotFound";
import { routeTree } from "src/routeTree.gen";

import { getIsomorphicRequestData } from "./lib/isomorphicRequestData/getIsomorphicRequestData";
import { createQueryClient } from "./queryClient";
import { type RootRouteContext } from "./types";
import "./staticData";

export function getRouter() {
  const { i18n } = getIsomorphicRequestData();
  const queryClient = createQueryClient();

  const router = createRouter({
    routeTree,
    defaultPreload: false,
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFound />,
    scrollRestoration: function ({ location }): boolean {
      return getScrollRestoration(router, location);
    },
    context: {
      queryClient: queryClient,
    } satisfies RootRouteContext,
    Wrap: ({ children }: PropsWithChildren) => {
      return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
    },

    // The amount of time in milliseconds that a route match's loader data will be considered fresh.
    // If a route match is matched again within this time frame, its loader data will not be reloaded.
    // defaults to 0 (data is always stale)
    defaultStaleTime: 0,

    // The amount of time in milliseconds that a route match's loader data will be kept in memory after a preload or it is no longer in use.
    // defaults to 30 minutes
    defaultGcTime: 0,

    // The threshold in milliseconds that a route must be pending before its pendingComponent is shown.
    // defaults to 1000
    defaultPendingMs: 1000,

    // The minimum amount of time in milliseconds that the pending component will be shown for if it is shown.
    // This is useful to prevent the pending component from flashing on the screen for a split second.
    // defaults to 500
    defaultPendingMinMs: 0,
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    hydrateOptions: {
      defaultOptions: {
        queries: {
          gcTime: 3_000,
        },
      },
    },
  });

  if (!router.isServer) {
    Sentry.addIntegration(
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    );
  }

  return router;
}

export type Router = Awaited<ReturnType<typeof getRouter>>;

function getScrollRestoration(
  router: AnyRouter,
  location: ParsedLocation,
): boolean {
  const { i18n, session } = getIsomorphicRequestData();
  const [matchedRoutes, params] = router.getMatchedRoutes(location.pathname);

  return matchedRoutes.every(function (route: AnyRoute) {
    return (
      route.options.staticData?.getScrollRestoration?.({
        i18n,
        session: session ?? null,
        location,
        pathname: location.pathname,
        params,
      }) ?? true
    );
  });
}
